export interface TranslationValue {
  description?: string;
  value: string;
  tags?: string[];
  translationStatus?: string;
}

export interface LanguageTranslations {
  [key: string]: TranslationValue;
}

export interface AllTranslations {
  en: LanguageTranslations;
  da?: LanguageTranslations;
  fr?: LanguageTranslations;
  de?: LanguageTranslations;
  it?: LanguageTranslations;
  ko?: LanguageTranslations;
  pl?: LanguageTranslations;
  ru?: LanguageTranslations;
  es?: LanguageTranslations;
  sv?: LanguageTranslations;
  nl?: LanguageTranslations;
  zh?: LanguageTranslations;
  pt?: LanguageTranslations;
}

export type Language = 'en' | 'da' | 'fr' | 'de' | 'it' | 'ko' | 'pl' | 'ru' | 'es' | 'sv' | 'nl' | 'zh' | 'pt';

function interpolateString(template: string, params: string[]): string {
  return template.replaceAll(/\{(\d+)\}/g, (match, index) => {
    const paramIndex = Number.parseInt(index, 10);
    return paramIndex < params.length ? params[paramIndex] : match;
  });
}

export interface Translator {
  tr: (key: string, ...params: string[]) => string;
  getValue: (key: string) => string | undefined;
}

export function createTranslator(language: Language, translations: AllTranslations): Translator {
  const tr = (key: string, ...params: string[]): string => {
    const lowerKey = key.toLowerCase();
    const languageTranslations = translations[language];
    const valueObj: TranslationValue | undefined = languageTranslations?.[lowerKey];
    const translationValue = valueObj?.value;

    if (translationValue) {
      return params.length > 0 ? interpolateString(translationValue, params) : translationValue;
    }

    const fallbackTranslations = translations.en;
    const fallbackValueObj: TranslationValue | undefined = fallbackTranslations?.[lowerKey];
    const fallbackTranslationValue = fallbackValueObj?.value;

    if (fallbackTranslationValue) {
      return params.length > 0 ? interpolateString(fallbackTranslationValue, params) : fallbackTranslationValue;
    }

    console.error(`Missing translation for key: '${key}' in language: '${language}'`);
    return key;
  };

  const getValue = (key: string): string | undefined => {
    const lowerKey = key.toLowerCase();
    const languageTranslations = translations[language];
    const translationValue = languageTranslations?.[lowerKey]?.value;
    if (translationValue) return translationValue;

    const fallbackTranslations = translations.en;
    const fallbackTranslationValue = fallbackTranslations?.[lowerKey]?.value;
    return fallbackTranslationValue;
  };

  return { tr, getValue };
}

class TranslationManager {
  private static instance: TranslationManager;
  private translator: Translator | null = null;

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor() {}

  static getInstance(): TranslationManager {
    if (!TranslationManager.instance) {
      TranslationManager.instance = new TranslationManager();
    }
    return TranslationManager.instance;
  }

  setTranslator(translator: Translator): void {
    this.translator = translator;
  }

  getTranslator(): Translator | null {
    return this.translator;
  }
}

const translationManager = TranslationManager.getInstance();

export function setGlobalTranslator(translator: Translator): void {
  translationManager.setTranslator(translator);
}

export function tr(key: string, ...params: string[]): string {
  const translator = translationManager.getTranslator();
  if (!translator) {
    console.warn(
      'Global translator not initialized. Translations may not work correctly outside React components. Call setGlobalTranslator() from TranslationsProvider.'
    );
    return key;
  }
  return translator.tr(key, ...params);
}

export function getValue(key: string): string | undefined {
  const translator = translationManager.getTranslator();
  if (!translator) {
    console.warn(
      'Global translator not initialized. Translations may not work correctly outside React components. Call setGlobalTranslator() from TranslationsProvider.'
    );
    return undefined;
  }
  return translator.getValue(key);
}
